import { useEffect, useRef, useState } from "preact/hooks";
import { Modal } from "./Modal";
import { idx, flash } from "../../store";
import type { Course } from "../../lib/types";
import { suggest, getConfig } from "../../lib/api";

declare global { interface Window { turnstile?: { render: (el: HTMLElement, o: Record<string, unknown>) => string; reset: (id?: string) => void } } }

function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  return new Promise((res, rej) => {
    const s = Object.assign(document.createElement("script"), { src: "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit", async: true });
    s.onload = () => res();
    s.onerror = () => rej(new Error("Turnstile"));
    document.head.appendChild(s);
  });
}

export function SuggestDialog({ course, onClose }: { course: Course; onClose: () => void }) {
  const I = idx.value!;
  const sess = I.sessionsByCourse.get(course.id) ?? [];
  const [target, setTarget] = useState<string>(sess[0]?.key ?? course.id);
  const [field, setField] = useState("start");
  const [val, setVal] = useState("");
  const [note, setNote] = useState("");
  const [contact, setContact] = useState("");
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [needsToken, setNeedsToken] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    getConfig().then(async ({ turnstileSiteKey }) => {
      if (!turnstileSiteKey || !box.current) return;
      setNeedsToken(true);
      await loadTurnstile();
      window.turnstile!.render(box.current, { sitekey: turnstileSiteKey, callback: (t: string) => setToken(t), "expired-callback": () => setToken(null) });
    }).catch(() => {});
  }, []);
  const isSession = target.includes("|");
  const fields = isSession ? ["start", "end", "day", "room", "lecturer", "group"] : ["name", "ects", "semester", "lecturers", "description", "other"];
  const cur = isSession ? (I.sessionsByKey.get(target) as unknown as Record<string, unknown>)?.[field] : (course as unknown as Record<string, unknown>)[field];
  async function send() {
    setBusy(true);
    try {
      await suggest({ target_type: isSession ? "session" : "course", target_id: target, field, old: cur == null ? null : String(cur), new: val, note, contact: contact || undefined, turnstileToken: token || undefined });
      flash("Hvala! Prijedlog je poslan na pregled.");
      onClose();
    } catch (e) { flash(`Greška: ${(e as Error).message}`); } finally { setBusy(false); }
  }
  return (
    <Modal title="Prijavi grešku" onClose={onClose}>
      <p class="note">Prijedlozi se ručno pregledaju prije objave. Najbolje je navesti izvor (npr. „profesorica je potvrdila emailom“).</p>
      <div class="grid2">
        <label>Šta<select class="field" value={target} onChange={(e) => setTarget((e.target as HTMLSelectElement).value)}>
          <option value={course.id}>Predmet (opći podaci)</option>
          {sess.map((s) => <option key={s.key} value={s.key}>{s.kind} {s.day} {s.start}–{s.end} {s.group || ""} {s.room || ""}</option>)}
        </select></label>
        <label>Polje<select class="field" value={field} onChange={(e) => setField((e.target as HTMLSelectElement).value)}>{fields.map((f) => <option key={f}>{f}</option>)}</select></label>
        <label>Trenutno<input class="field" value={cur == null ? "" : String(cur)} readOnly /></label>
        <label>Ispravno<input class="field" value={val} onInput={(e) => setVal((e.target as HTMLInputElement).value)} placeholder={field === "start" ? "08:30" : ""} /></label>
      </div>
      <label style="display:grid;gap:.2rem;margin-top:.5rem">Napomena / izvor<textarea style="min-height:70px" value={note} onInput={(e) => setNote((e.target as HTMLTextAreaElement).value)} /></label>
      <label style="display:grid;gap:.2rem;margin-top:.5rem">Kontakt (opcionalno, briše se nakon pregleda)<input class="field" value={contact} onInput={(e) => setContact((e.target as HTMLInputElement).value)} /></label>
      <div ref={box} class="row" />
      <div class="row"><button class="btn" disabled={busy || !val.trim() || (needsToken && !token)} onClick={send}>Pošalji</button></div>
    </Modal>
  );
}
